/**
 * Admin — AICon Sync Panel
 *
 * `/admin/aicon-sync`
 *
 * A read-only console for the `aicon_submission_logs` collection with:
 *   - Filter tabs (All / Failed / Success)
 *   - Endpoint + text search + loan_case_id filter
 *   - Paginated table with expandable rows (full request/response JSON)
 *   - One-click retry for a loan_case (POST /api/admin/aicon-sync/retry/{id})
 *
 * Only visible to admin / super_admin / operation_admin / account roles.
 */
import React, { useEffect, useMemo, useState } from 'react';
import api from '../../lib/api';
import { toast, Toaster } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  RefreshCw, ChevronDown, ChevronRight, CheckCircle2, XCircle, Loader2,
  Search, Copy, Activity, ExternalLink,
} from 'lucide-react';

const FILTER_TABS = [
  { key: 'all', label: 'All' },
  { key: 'failed', label: 'Failed' },
  { key: 'success', label: 'Success' },
];

const RESULT_STYLES = {
  success: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  failed: 'bg-red-100 text-red-800 border-red-200',
};

export default function AdminAiconSync() {
  const [stats, setStats] = useState(null);
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [filterResult, setFilterResult] = useState('all');
  const [search, setSearch] = useState('');
  const [endpoint, setEndpoint] = useState('');
  const [loanCaseFilter, setLoanCaseFilter] = useState('');
  const [skip, setSkip] = useState(0);
  const [limit] = useState(25);
  const [expanded, setExpanded] = useState({});
  const [retryingIds, setRetryingIds] = useState({});

  const loadStats = async () => {
    try {
      const r = await api.get('/admin/aicon-sync/stats');
      setStats(r.data);
    } catch (e) {
      console.error('stats load failed', e);
    }
  };

  const loadLogs = async () => {
    try {
      const params = { limit, skip };
      if (filterResult !== 'all') params.result = filterResult;
      if (endpoint.trim()) params.endpoint = endpoint.trim();
      if (loanCaseFilter.trim()) params.loan_case_id = loanCaseFilter.trim();
      if (search.trim()) params.q = search.trim();
      const r = await api.get('/admin/aicon-sync/logs', { params });
      setLogs(r.data?.items || []);
      setTotal(r.data?.total || 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load logs');
    }
  };

  const reload = async () => {
    setRefreshing(true);
    await Promise.all([loadStats(), loadLogs()]);
    setRefreshing(false);
  };

  useEffect(() => {
    (async () => { setLoading(true); await reload(); setLoading(false); })();
  }, [filterResult, skip]);

  const applyFilters = async () => {
    setSkip(0);
    await loadLogs();
  };

  const toggleExpand = (id) => setExpanded(p => ({ ...p, [id]: !p[id] }));

  const retry = async (loanCaseId) => {
    if (!loanCaseId) return;
    setRetryingIds(p => ({ ...p, [loanCaseId]: true }));
    try {
      const r = await api.post(`/admin/aicon-sync/retry/${loanCaseId}`);
      const outcome = r.data?.outcome || {};
      if (r.data?.success) {
        toast.success(`Retry succeeded — status: ${outcome.sync_status}`);
      } else {
        toast.error(`Retry finished — status: ${outcome.sync_status}. ${outcome.error_message || ''}`);
      }
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Retry failed');
    } finally {
      setRetryingIds(p => ({ ...p, [loanCaseId]: false }));
    }
  };

  const copyToClipboard = (obj) => {
    try {
      navigator.clipboard.writeText(JSON.stringify(obj, null, 2));
      toast.success('Copied to clipboard');
    } catch {
      toast.error('Copy failed');
    }
  };

  const pages = Math.ceil(total / limit) || 1;
  const currentPage = Math.floor(skip / limit) + 1;

  const caseCounts = stats?.loan_cases || {};
  const attemptCounts = stats?.attempts_by_result || {};

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-aicon-sync-page">
      <Toaster richColors position="top-center" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-start justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Activity className="w-6 h-6 text-indigo-600" /> AICon Sync
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Audit trail of every outbound call to AICon (create case + document upload) with one-click retry.
            </p>
          </div>
          <Button variant="outline" onClick={reload} disabled={refreshing} data-testid="aicon-sync-refresh">
            <RefreshCw className={`w-4 h-4 mr-1 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
          <StatCard label="Loan Cases · Synced" value={caseCounts.synced || 0} accent="emerald" />
          <StatCard label="Loan Cases · Failed" value={caseCounts.failed || 0} accent="red" />
          <StatCard label="Not Configured" value={caseCounts.not_configured || 0} accent="gray" />
          <StatCard label="API Success" value={attemptCounts.success || 0} accent="emerald" />
          <StatCard label="API Failed" value={attemptCounts.failed || 0} accent="red" />
        </div>

        {/* Filters */}
        <Card className="mb-4">
          <CardContent className="pt-6">
            <div className="flex gap-2 mb-4 flex-wrap">
              {FILTER_TABS.map(t => (
                <button
                  key={t.key}
                  onClick={() => { setFilterResult(t.key); setSkip(0); }}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                    filterResult === t.key
                      ? 'bg-indigo-600 text-white border-indigo-600'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                  data-testid={`aicon-sync-tab-${t.key}`}
                >
                  {t.label}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
              <div className="sm:col-span-2 relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && applyFilters()}
                  placeholder="Search error message / endpoint / aicon_case_ref"
                  className="pl-9"
                  data-testid="aicon-sync-search"
                />
              </div>
              <Input
                value={endpoint}
                onChange={e => setEndpoint(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && applyFilters()}
                placeholder="Endpoint contains (e.g. /v1/cases)"
                data-testid="aicon-sync-endpoint-filter"
              />
              <div className="flex gap-2">
                <Input
                  value={loanCaseFilter}
                  onChange={e => setLoanCaseFilter(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && applyFilters()}
                  placeholder="loan_case_id"
                  data-testid="aicon-sync-lc-filter"
                />
                <Button onClick={applyFilters} data-testid="aicon-sync-apply">
                  Apply
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center justify-between">
              <span>Submission Logs</span>
              <span className="text-sm font-normal text-gray-500">
                {total} row{total === 1 ? '' : 's'}
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-10 text-gray-500">
                <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
                Loading logs…
              </div>
            ) : logs.length === 0 ? (
              <p className="text-center text-gray-500 py-10">No logs found for the current filters.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="border-b bg-gray-50">
                    <tr className="text-left">
                      <th className="p-2 w-8"></th>
                      <th className="p-2">When</th>
                      <th className="p-2">Endpoint</th>
                      <th className="p-2">Status</th>
                      <th className="p-2">Loan Case</th>
                      <th className="p-2">AICon Case</th>
                      <th className="p-2">Duration</th>
                      <th className="p-2">Result</th>
                      <th className="p-2 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map(row => (
                      <React.Fragment key={row.id}>
                        <tr
                          className="border-b hover:bg-gray-50 cursor-pointer"
                          onClick={() => toggleExpand(row.id)}
                          data-testid={`aicon-log-row-${row.id}`}
                        >
                          <td className="p-2">
                            {expanded[row.id] ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                          </td>
                          <td className="p-2 whitespace-nowrap text-xs text-gray-600">
                            {formatDate(row.submitted_at)}
                          </td>
                          <td className="p-2 font-mono text-xs text-gray-700">
                            {row.endpoint || <span className="text-gray-400 italic">(local save)</span>}
                          </td>
                          <td className="p-2">
                            <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                              (row.http_status || 0) >= 200 && (row.http_status || 0) < 300
                                ? 'bg-emerald-50 text-emerald-700'
                                : (row.http_status || 0) >= 400
                                ? 'bg-red-50 text-red-700'
                                : 'bg-gray-100 text-gray-600'
                            }`}>
                              {row.http_status || '—'}
                            </span>
                          </td>
                          <td className="p-2 font-mono text-xs">
                            <span className="truncate max-w-[140px] inline-block align-middle" title={row.loan_case_id}>
                              {row.loan_case_id ? `${row.loan_case_id.slice(0, 8)}…` : '—'}
                            </span>
                          </td>
                          <td className="p-2 text-xs">
                            {row.aicon_case_ref || (row.aicon_case_id ? row.aicon_case_id.slice(0, 12) + '…' : '—')}
                          </td>
                          <td className="p-2 text-xs text-gray-600">
                            {row.duration_ms ? `${row.duration_ms}ms` : '—'}
                          </td>
                          <td className="p-2">
                            <Badge className={`text-xs ${RESULT_STYLES[row.submission_result] || 'bg-gray-100 text-gray-700'}`}>
                              {row.submission_result === 'success'
                                ? <CheckCircle2 className="w-3 h-3 mr-1 inline" />
                                : <XCircle className="w-3 h-3 mr-1 inline" />}
                              {row.submission_result}
                            </Badge>
                          </td>
                          <td className="p-2 text-right" onClick={e => e.stopPropagation()}>
                            {row.loan_case_id && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => retry(row.loan_case_id)}
                                disabled={!!retryingIds[row.loan_case_id]}
                                data-testid={`aicon-retry-${row.id}`}
                              >
                                {retryingIds[row.loan_case_id]
                                  ? <Loader2 className="w-3 h-3 animate-spin" />
                                  : <RefreshCw className="w-3 h-3" />}
                                <span className="ml-1">Retry</span>
                              </Button>
                            )}
                          </td>
                        </tr>
                        {expanded[row.id] && (
                          <tr className="bg-gray-50/50">
                            <td colSpan={9} className="p-4">
                              <LogDetail row={row} onCopy={copyToClipboard} />
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {logs.length > 0 && (
              <div className="flex items-center justify-between pt-4 border-t mt-4">
                <p className="text-sm text-gray-500">Page {currentPage} of {pages}</p>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={skip === 0}
                    onClick={() => setSkip(Math.max(0, skip - limit))}
                    data-testid="aicon-sync-prev-page"
                  >
                    Previous
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={skip + limit >= total}
                    onClick={() => setSkip(skip + limit)}
                    data-testid="aicon-sync-next-page"
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// ─────────── Sub-components ───────────
function StatCard({ label, value, accent }) {
  const styles = {
    emerald: 'border-emerald-200 bg-emerald-50 text-emerald-800',
    red: 'border-red-200 bg-red-50 text-red-800',
    gray: 'border-gray-200 bg-gray-50 text-gray-700',
  }[accent] || 'border-gray-200 bg-white text-gray-800';
  return (
    <div className={`rounded-xl border p-4 ${styles}`}>
      <p className="text-xs uppercase tracking-wider font-medium opacity-80">{label}</p>
      <p className="text-2xl font-bold mt-1">{value}</p>
    </div>
  );
}

function LogDetail({ row, onCopy }) {
  return (
    <div className="space-y-3">
      {row.error_message && (
        <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-800">
          <p className="font-medium">Error</p>
          <p className="mt-0.5 font-mono text-xs whitespace-pre-wrap">{row.error_message}</p>
        </div>
      )}
      <DetailRow label="loan_case_id" value={row.loan_case_id} mono />
      <DetailRow label="user_id" value={row.user_id || '—'} mono />
      <DetailRow label="aicon_case_id / ref" value={`${row.aicon_case_id || '—'}  /  ${row.aicon_case_ref || '—'}`} mono />
      <DetailRow label="method / endpoint" value={`${row.http_method || '—'}  ${row.endpoint || '—'}`} mono />
      <DetailRow label="uploaded documents" value={JSON.stringify(row.uploaded_document_names || {})} mono />

      <JsonBlock title="Request Payload" data={row.submitted_payload} onCopy={onCopy} />
      <JsonBlock title="Request Headers (redacted)" data={row.request_headers} onCopy={onCopy} />
      <JsonBlock title="Response Body" data={row.response_body} onCopy={onCopy} />
    </div>
  );
}

function DetailRow({ label, value, mono }) {
  return (
    <div className="flex items-baseline gap-3">
      <span className="text-xs text-gray-500 uppercase w-40 flex-shrink-0">{label}</span>
      <span className={`text-sm ${mono ? 'font-mono' : ''} text-gray-800 break-all`}>{value}</span>
    </div>
  );
}

function JsonBlock({ title, data, onCopy }) {
  if (data === null || data === undefined) return null;
  const isEmpty = typeof data === 'object' && Object.keys(data || {}).length === 0;
  return (
    <div className="border rounded-lg bg-white">
      <div className="flex items-center justify-between px-3 py-2 border-b bg-gray-50">
        <p className="text-xs font-semibold text-gray-700">{title}</p>
        <button
          onClick={() => onCopy(data)}
          className="text-xs text-indigo-600 hover:text-indigo-800 flex items-center gap-1"
        >
          <Copy className="w-3 h-3" /> Copy
        </button>
      </div>
      <pre className="p-3 text-[11px] font-mono text-gray-700 overflow-x-auto max-h-64">
        {isEmpty ? '(empty)' : JSON.stringify(data, null, 2)}
      </pre>
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-MY', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
  } catch { return iso; }
}
